import React from 'react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Shield, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';

const EstadisticasPage = () => {
  const { mockData } = useAuth();

  const garantiasPorNivel = React.useMemo(() => {
    if (!mockData?.garantias) return [];
    
    const niveles = { 'Básica': 0, 'Estándar': 0, 'Premium': 0, 'Total': 0 };
    mockData.garantias.forEach(g => {
      niveles[g.nivelNombre] = (niveles[g.nivelNombre] || 0) + 1;
    });

    return Object.entries(niveles).map(([nivel, cantidad]) => ({
      nivel,
      cantidad
    }));
  }, [mockData]);

  const ingresosVsCostes = React.useMemo(() => {
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return meses.map((mes) => ({
      mes,
      ingresos: Math.random() * 50000 + 30000,
      costes: Math.random() * 30000 + 15000
    }));
  }, []);

  const averiasPorMes = React.useMemo(() => {
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'];
    return meses.map((mes) => ({
      mes,
      cantidad: Math.floor(Math.random() * 30) + 10
    }));
  }, []);

  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6'];

  const totalIngresos = mockData?.garantias.reduce((sum, g) => sum + g.precio, 0) || 0;
  const totalCostes = mockData?.averias.reduce((sum, a) => sum + (a.costeReal || a.costeEstimado), 0) || 0;
  const beneficio = totalIngresos - totalCostes;
  const margen = totalIngresos > 0 ? (beneficio / totalIngresos) * 100 : 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Estadísticas</h1>
          <p className="text-gray-600 mt-1">Análisis y métricas del negocio</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Ingresos</p>
                <p className="text-2xl font-bold text-success-600">{formatCurrency(totalIngresos)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-success-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Costes</p>
                <p className="text-2xl font-bold text-danger-600">{formatCurrency(totalCostes)}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-danger-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Beneficio Neto</p>
                <p className="text-2xl font-bold text-primary-600">{formatCurrency(beneficio)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Margen</p>
                <p className="text-2xl font-bold text-gray-900">{margen.toFixed(1)}%</p>
              </div>
              <Shield className="w-8 h-8 text-gray-600" />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Ingresos vs Costes" subtitle="Últimos 12 meses">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ingresosVsCostes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="ingresos" fill="#22c55e" name="Ingresos" />
                <Bar dataKey="costes" fill="#ef4444" name="Costes" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Garantías por Nivel" subtitle="Distribución actual">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={garantiasPorNivel}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ nivel, percent }) => `${nivel} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="cantidad"
                >
                  {garantiasPorNivel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card title="Evolución de Averías" subtitle="Últimos 6 meses">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={averiasPorMes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="cantidad" stroke="#ef4444" strokeWidth={2} name="Averías" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </Layout>
  );
};

export default EstadisticasPage;
